import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { ReactNode, useState } from "react";

export type TrashResourceKind =
  | "workspace"
  | "canvas"
  | "chat"
  | "file"
  | "skill";

interface ConfirmTrashDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  kind: TrashResourceKind;
  /** Resource name, quoted in the title when provided. */
  name?: string;
  /** Called when the user confirms. The dialog closes after it resolves. */
  onConfirm: () => Promise<unknown> | void;
  /** Extra consequence content (e.g. published-file warning). */
  children?: ReactNode;
  /**
   * When false, the dialog stays open after confirm; the host closes via
   * onOpenChange(false). Default: true.
   */
  closeOnConfirm?: boolean;
  /** External working state (host-driven async after confirm). */
  isWorking?: boolean;
}

const COPY: Record<TrashResourceKind, { title: string; body: string }> = {
  workspace: {
    title: "Archive this Workspace?",
    body: "The Workspace and its Canvases, chats, and files will be archived. You can restore it any time.",
  },
  canvas: {
    title: "Archive this Canvas?",
    body: "The Canvas and its chats and files will be archived. You can restore it any time.",
  },
  chat: {
    title: "Archive this chat?",
    body: "The chat and its files will be archived. You can restore it any time.",
  },
  file: {
    title: "Archive this file?",
    body: "The file will be archived. You can restore it for 14 days.",
  },
  skill: {
    title: "Archive this skill?",
    body: "The skill will be archived. You can restore it any time.",
  },
};

/**
 * Shared confirmation for every archive action (workspaces, canvases, chats,
 * files). Follows the design-system dialog spec: title names the action as a
 * question, body is one consequence sentence, the primary button is always
 * "Archive", secondary is Cancel.
 */
export function ConfirmTrashDialog({
  open,
  onOpenChange,
  kind,
  name,
  onConfirm,
  children,
  closeOnConfirm = true,
  isWorking = false,
}: ConfirmTrashDialogProps) {
  const [isPending, setIsPending] = useState(false);
  const copy = COPY[kind];
  const busy = isPending || isWorking;

  const handleConfirm = async () => {
    if (!closeOnConfirm) {
      onConfirm();
      return;
    }

    setIsPending(true);
    try {
      await onConfirm();
      onOpenChange(false);
    } finally {
      setIsPending(false);
    }
  };

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (busy) return;
        onOpenChange(next);
      }}
    >
      <AlertDialogContent
        className="sm:max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <AlertDialogHeader>
          <AlertDialogTitle>
            {name ? `Archive "${name}"?` : copy.title}
          </AlertDialogTitle>
          <AlertDialogDescription>{copy.body}</AlertDialogDescription>
        </AlertDialogHeader>
        {children}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={busy}
            isPending={busy}
          >
            Archive
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
